import { useFrame } from "@react-three/fiber";
import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type BufferGeometry,
  DoubleSide,
  FrontSide,
  Matrix4,
  type Mesh,
  type Object3D,
  Quaternion,
  Vector3,
} from "three";
import {
  HOVER_GLOW_RENDER_ORDER,
  xrayGlowPassOrders,
  xrayGlowSlots,
} from "./glowOcclusionSlots";
import { getXrayGlowPassMaterials } from "./glowPassMaterials";
import { createGlowTintMaterial, type GlowVariant } from "./glowTintMaterial";
import { isRealPartMesh, meshSignature } from "./partGeometryFit";

type MeshClone = {
  key: string;
  geometry: BufferGeometry;
  position: [number, number, number];
  quaternion: [number, number, number, number];
  scale: [number, number, number];
  /** Whether the source mesh is drawn from both sides. */
  doubleSided: boolean;
};

/**
 * Whether a mesh is drawn from both sides. Parts modelled as open shells - a
 * folding handle whose mesh is a single skin with no back - are drawn
 * double-sided so they look solid from anywhere. A glow that culled back faces
 * would simply vanish for them as soon as the camera passed to the other side,
 * which is exactly when a highlight is needed most.
 */
function isDoubleSided(material: Mesh["material"]): boolean {
  const materials = Array.isArray(material) ? material : [material];
  return materials.some((entry) => entry && entry.side !== FrontSide);
}

type GlowFit = {
  /** Local transform of the part group, mirrored onto the highlight group. */
  root: {
    position: [number, number, number];
    quaternion: [number, number, number, number];
    scale: [number, number, number];
  };
  meshes: MeshClone[];
};

/** Props for {@link DragHighlightGlow}. */
export interface DragHighlightGlowProps {
  /** The draggable part group to wrap with a glow. */
  target: Object3D | null;
  /** When true the glow gently pulses (the "armed" discovery state). */
  pulse?: boolean;
  /**
   * How strongly to draw: the thin `"outline"` every armed part carries, or
   * the `"full"` tint of the one part under attention. Switching between the
   * two swaps the tint materials only; the x-ray slot and the depth passes are
   * shared by both, so a part changes strength without a flash.
   */
  variant?: GlowVariant;
  /** A value that changes whenever the target moves, to recompute the clones. */
  refitKey?: unknown;
  /**
   * Draw the glow through anything in front of the part, so one mounted on the
   * far side of the case stays visible. The one exception is the part's own
   * `occlusionGroup`: its siblings hide it the way solid objects do. Off by
   * default: a glow that merely marks what is under the pointer has nothing to
   * reveal, since the pointer is on a surface the user can already see.
   */
  xray?: boolean;
  /**
   * Parts that share this key occlude each other's x-ray glow while everything
   * else still shines through. Meant for the parts one option emits - the left
   * and right handle of a mirrored preset, the feet of a grid - whose glows
   * otherwise stack into a single blue blur where they overlap on screen.
   * Ignored unless `xray` is set; an x-ray glow without a group is occluded by
   * nothing but itself.
   */
  occlusionGroup?: string;
}

/**
 * Builds lightweight render-only clones of every mesh under `target`, reusing
 * their geometries (no buffer copies). The clones are placed in a sibling group
 * that mirrors the part group's local transform, so the glow lines up exactly
 * with the part while living in the parent frame (correct under translated/
 * rotated ancestors like the floor aligner).
 */
function collectMeshClones(target: Object3D): GlowFit | null {
  target.updateWorldMatrix(true, true);

  const rootInverse = new Matrix4().copy(target.matrixWorld).invert();
  const tmpMatrix = new Matrix4();
  const tmpPos = new Vector3();
  const tmpQuat = new Quaternion();
  const tmpScale = new Vector3();

  const meshes: MeshClone[] = [];
  target.traverse((obj) => {
    // Drag helpers and the stand-in a part shows while its model downloads are
    // both left out - see `isRealPartMesh`. Glowing the placeholder announces
    // the part as a crate until the mesh lands; worse, the clone would keep
    // that shape afterwards, because a finished download re-renders nothing
    // here.
    if (!isRealPartMesh(obj)) return;
    const mesh = obj;

    // Transform of this mesh relative to the part group.
    tmpMatrix.multiplyMatrices(rootInverse, mesh.matrixWorld);
    tmpMatrix.decompose(tmpPos, tmpQuat, tmpScale);
    meshes.push({
      key: `${mesh.geometry.uuid}-${meshes.length}`,
      geometry: mesh.geometry,
      doubleSided: isDoubleSided(mesh.material),
      position: [tmpPos.x, tmpPos.y, tmpPos.z],
      quaternion: [tmpQuat.x, tmpQuat.y, tmpQuat.z, tmpQuat.w],
      scale: [tmpScale.x, tmpScale.y, tmpScale.z],
    });
  });

  if (meshes.length === 0) return null;

  return {
    root: {
      position: [target.position.x, target.position.y, target.position.z],
      quaternion: [
        target.quaternion.x,
        target.quaternion.y,
        target.quaternion.z,
        target.quaternion.w,
      ],
      scale: [target.scale.x, target.scale.y, target.scale.z],
    },
    meshes,
  };
}

/**
 * Holds a render-order slot for `group` while mounted. Ref-counted in the
 * registry, so the parts of one group share a slot and React StrictMode's
 * double effect run is harmless. The slot arrives one commit after mount; the
 * caller draws nothing until then rather than one frame of ungrouped glow.
 */
function useOcclusionSlot(group: string | null): number | null {
  const [held, setHeld] = useState<{ group: string; slot: number } | null>(
    null,
  );
  useEffect(() => {
    if (group === null) {
      setHeld(null);
      return;
    }
    setHeld({ group, slot: xrayGlowSlots.acquire(group) });
    return () => {
      xrayGlowSlots.release(group);
    };
  }, [group]);
  return held !== null && held.group === group ? held.slot : null;
}

const noRaycast = () => null;
const DRAG_HELPER_USER_DATA = { isDragHelper: true };

/**
 * Renders a soft glowing aura around a draggable part to signal it can be moved.
 * Follows the part's actual silhouette via a fresnel rim shader (no wireframe
 * box), and with `xray` through whatever is in front of the part except the
 * part's own `occlusionGroup`. The mesh-clone rebuild runs only when the
 * target/position changes; the per-frame cost is a single uniform update, and
 * only while pulsing.
 *
 * The x-ray glow is not drawn with the depth test off. It is depth-tested like
 * any other glow, against a depth buffer that two colour-less passes over the
 * same clones have rewritten first: inside the group's silhouette it then
 * holds the group's own nearest surface instead of the case wall's, so the
 * glow passes wherever the case would have hidden it and fails only behind a
 * sibling. The passes of every part in a group share three render orders, see
 * `glowOcclusionSlots.ts`, which is what lets independent components build
 * one buffer between them.
 */
export const DragHighlightGlow = ({
  target,
  pulse = false,
  refitKey,
  xray = false,
  occlusionGroup,
  variant = "full",
}: DragHighlightGlowProps) => {
  const [fit, setFit] = useState<GlowFit | null>(null);

  const privateGroup = useId();
  const slot = useOcclusionSlot(xray ? (occlusionGroup ?? privateGroup) : null);

  // One material per sidedness, so each clone can be drawn the way its source
  // mesh is. Two at most, and the one that goes unused never compiles a shader.
  const materials = useMemo(
    () => ({
      front: createGlowTintMaterial(FrontSide, xray, variant),
      double: createGlowTintMaterial(DoubleSide, xray, variant),
    }),
    [xray, variant],
  );

  const signatureRef = useRef("");
  const refit = useCallback(() => {
    signatureRef.current = meshSignature(target);
    setFit(target ? collectMeshClones(target) : null);
  }, [target]);

  useEffect(() => {
    void refitKey;
    refit();
  }, [refit, refitKey]);

  // Catches a model landing after the clones were taken. Comparing a handful of
  // geometry ids per part is far cheaper than the render it guards.
  useFrame(() => {
    if (meshSignature(target) !== signatureRef.current) refit();
  });

  useEffect(() => {
    return () => {
      materials.front.dispose();
      materials.double.dispose();
    };
  }, [materials]);

  // Only the pulse animates; a steady glow would otherwise write the same
  // uniform every frame for every highlighted part on the case. It scales the
  // material's own opacity rather than replacing it, so the tint targets above
  // stay the ceiling whichever sidedness the clone uses.
  const baseOpacityRef = useRef<{ front: number; double: number }>({
    front: 0,
    double: 0,
  });
  useEffect(() => {
    baseOpacityRef.current = {
      front: materials.front.uniforms.uOpacity?.value ?? 0,
      double: materials.double.uniforms.uOpacity?.value ?? 0,
    };
  }, [materials]);

  useFrame((state) => {
    if (!pulse) return;
    const scale =
      0.6 + 0.4 * (0.5 + 0.5 * Math.sin(state.clock.elapsedTime * 4));
    const uniforms = [
      [materials.front, baseOpacityRef.current.front] as const,
      [materials.double, baseOpacityRef.current.double] as const,
    ];
    for (const [material, base] of uniforms) {
      const opacity = material.uniforms.uOpacity;
      if (opacity) opacity.value = base * scale;
    }
    // The pulse is the only thing animating, so the demand loop has to be
    // asked for the next frame explicitly.
    if (fit) state.invalidate();
  });

  if (!fit) return null;
  if (xray && slot === null) return null;

  const orders = xray && slot !== null ? xrayGlowPassOrders(slot) : null;
  const passes = orders ? getXrayGlowPassMaterials() : null;

  return (
    <group
      position={fit.root.position}
      quaternion={fit.root.quaternion}
      scale={fit.root.scale}
      userData={DRAG_HELPER_USER_DATA}
    >
      {fit.meshes.map((clone) => {
        const glow = clone.doubleSided ? materials.double : materials.front;
        const shared = {
          geometry: clone.geometry,
          position: clone.position,
          quaternion: clone.quaternion,
          scale: clone.scale,
          raycast: noRaycast,
          userData: DRAG_HELPER_USER_DATA,
        };
        if (!orders || !passes) {
          return (
            <mesh
              key={clone.key}
              {...shared}
              material={glow}
              renderOrder={HOVER_GLOW_RENDER_ORDER}
            />
          );
        }
        return (
          <Fragment key={clone.key}>
            <mesh
              {...shared}
              material={passes.reset}
              renderOrder={orders.reset}
            />
            <mesh
              {...shared}
              material={passes.depth}
              renderOrder={orders.depth}
            />
            <mesh {...shared} material={glow} renderOrder={orders.glow} />
          </Fragment>
        );
      })}
    </group>
  );
};

export default DragHighlightGlow;
